type AllowedStringKey = (key: string) => boolean;

export type AllowedOwnStringEntry = readonly [key: string, value: unknown];

/**
 * Return the object's own keys, or `null` if any of them is illegitimate.
 *
 * Own property names and symbols are read rather than only enumerable keys so an internal
 * identifier cannot slip past the sentinel. Enumerability matters on its own: a
 * non-enumerable own property is invisible to `JSON.stringify`, so a record that "has" a
 * required field non-enumerably would serialize onto the wire without it. The only legitimate
 * non-enumerable own key on these shapes is an array's `length`, which a caller opts into
 * through `isAllowedNonEnumerableKey`.
 */
export function readAllowedOwnStringKeys(
  value: object,
  isAllowedStringKey: AllowedStringKey,
  isAllowedNonEnumerableKey: AllowedStringKey = () => false
): readonly string[] | null {
  const entries = readAllowedOwnStringEntries(value, isAllowedStringKey, isAllowedNonEnumerableKey);
  return entries?.map(([key]) => key) ?? null;
}

/**
 * Snapshot allowed own data properties without invoking caller-controlled getters.
 *
 * The value comes from the data descriptor rather than `record[key]`, so validation and DTO
 * construction can share one stable local snapshot. Accessors are rejected even when they are
 * enumerable: their value is not stable wire data.
 */
export function readAllowedOwnStringEntries(
  value: object,
  isAllowedStringKey: AllowedStringKey,
  isAllowedNonEnumerableKey: AllowedStringKey = () => false
): readonly AllowedOwnStringEntry[] | null {
  const ownKeys = readOwnKeys(value);
  if (ownKeys === null) return null;
  const allowed: AllowedOwnStringEntry[] = [];
  for (const key of ownKeys) {
    if (typeof key !== 'string') return null;
    if (!isAllowedStringKey(key)) return null;
    const descriptor = readEnumerableDataDescriptor(value, key, isAllowedNonEnumerableKey);
    if (descriptor === null) return null;
    allowed.push([key, descriptor.value]);
  }
  return allowed;
}

export function isPlainDataRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return !Array.isArray(value) && (prototype === Object.prototype || prototype === null);
  } catch {
    return false;
  }
}

export function isPlainDataArray(value: unknown): value is readonly unknown[] {
  if (typeof value !== 'object' || value === null) return false;
  try {
    return Array.isArray(value) && Object.getPrototypeOf(value) === Array.prototype;
  } catch {
    return false;
  }
}

function readOwnKeys(value: object): readonly PropertyKey[] | null {
  try {
    return [
      ...Object.getOwnPropertyNames(value),
      ...Object.getOwnPropertySymbols(value),
    ];
  } catch {
    return null;
  }
}

function readEnumerableDataDescriptor(
  value: object,
  key: string,
  isAllowedNonEnumerableKey: AllowedStringKey
): PropertyDescriptor | null {
  const descriptor = readOwnPropertyDescriptor(value, key);
  if (descriptor === undefined) return null;
  if (descriptor === null) return null;
  if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
  if (
    Object.prototype.hasOwnProperty.call(descriptor, 'get') ||
    Object.prototype.hasOwnProperty.call(descriptor, 'set')
  ) {
    return null;
  }
  if (descriptor.enumerable !== true && !isAllowedNonEnumerableKey(key)) return null;
  return descriptor;
}

function readOwnPropertyDescriptor(
  value: object,
  key: string
): PropertyDescriptor | undefined | null {
  try {
    return Object.getOwnPropertyDescriptor(value, key);
  } catch {
    return null;
  }
}
